import { faqsPl } from "@data/mvp-app-device-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  mvpClusterLinks,
  mvpKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/mvp-landing/shared";
import {
  Calendar,
  CheckCircle,
  Code,
  Cpu,
  FileText,
  Layers,
  LifeBuoy,
  PenTool,
  Rocket,
  Search,
  Smartphone,
  Sparkles,
  Target,
  Users,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "MVP aplikacji lub urządzenia - szybka walidacja pomysłu";

export const description =
  "MVP aplikacji mobilnej, webowej, SaaS lub urządzenia IoT — szybka walidacja pomysłu z użytkownikami. Polski software house buduje wąski zakres pod realny feedback z rynku.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "MVP aplikacji lub urządzenia",
    description,
    "MVP application and IoT device development",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "MVP aplikacji",
  headerTail: "lub urządzenia",
  description:
    "Budujemy pierwszą wersję produktu do walidacji z użytkownikami — aplikację mobilną, webową, SaaS albo urządzenie IoT. Ten sam proces: wąski zakres, jasne kryteria sukcesu, szybki feedback.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj proces",
    href: "#proces",
  },
  benefits: [
    { text: "Mobile, web, SaaS, IoT", Icon: CheckCircle },
    { text: "Walidacja z użytkownikami", Icon: CheckCircle },
    { text: "Zakres pod budżet startupu", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest MVP aplikacji lub urządzenia?",
    p: "Na pierwszym spotkaniu rzadko chodzi o „pełny produkt”. Zwykle potrzebujesz prostego MVP do testu z użytkownikami, orientacyjnej wyceny, timeline’u i partnera, który pomoże uciąć zakres. Czy to brzmi jak Twoja sytuacja?",
  },
  points: [
    {
      Icon: Rocket,
      title: "Startujesz projekt i chcesz szybko przetestować pomysł",
      description:
        "Potrzebujesz działającego MVP — edukacja, SaaS, marketplace, aplikacja B2C — żeby zbierać feedback, nie prezentację. Zakres tnemy do jednej ścieżki użytkownika.",
    },
    {
      Icon: Smartphone,
      title: "Nie wiesz jeszcze, czy to appka mobilna, web czy urządzenie",
      description:
        "Proces budowy MVP jest ten sam. Pomagamy wybrać formę produktu pod hipotezę biznesową, budżet i czas do pierwszego testu.",
    },
    {
      Icon: Sparkles,
      title: "Chcesz MVP z AI, ale bez przepalania budżetu na „wszystko”",
      description:
        "Integracja OpenAI, rekomendacje, chatbot — traktujemy AI jako funkcję MVP, nie osobny projekt. Najpierw hipoteza, potem model i UI.",
    },
    {
      Icon: Cpu,
      title: "Masz pomysł na produkt connected / wearable / IoT",
      description:
        "Zaczynamy od DevKit i wąskiego PoC/MVP (firmware + app), zanim inwestujesz we własną PCB i produkcję.",
    },
    {
      Icon: FileText,
      title: "Masz Figmę lub brief i potrzebujesz wyceny oraz zespołu",
      description:
        "Przygotowujemy ofertę, architekturę i plan prac. Jeśli brief jest niepełny — najpierw warsztaty produktowe, potem stała wycena albo praca w modelu Time & Material.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opowiedz nam o pomyśle — nawet jeśli nie masz jeszcze pełnej specyfikacji. Zaproponujemy zakres MVP albo warsztaty discovery.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto budować MVP z nami?",
    p: "Jesteśmy polskim software house’em, który łączy produkt software z IoT i BLE. Dzięki temu MVP aplikacji i MVP urządzenia prowadzimy tym samym rygorem zakresu — bez rozpraszania odpowiedzialności między trzema dostawcami.",
    imageAlt: "MVP aplikacji lub urządzenia — walidacja pomysłu",
  },
  values: [
    {
      Icon: Target,
      title: "Wąski zakres pod hipotezę, nie pod listę życzeń",
      description:
        "Każda funkcja musi bronić się kryterium sukcesu. Domyślnie budujemy proste MVP do testu z użytkownikami — nie wersję „na wszystko”.",
    },
    {
      Icon: Layers,
      title: "Jeden proces: app, SaaS i urządzenie",
      description:
        "Forma produktu wynika z Twojej hipotezy biznesowej, nie z katalogu usług. Ten sam rygor zakresu działa dla web, mobile i IoT.",
    },
    {
      Icon: Users,
      title: "Discovery i warsztaty przed kosztownym developmentem",
      description:
        "Gdy brief jest niepełny, zaczynamy od warsztatów: user stories, ryzyka, makiety low-fi i materiały do wyceny.",
    },
    {
      Icon: Cpu,
      title: "MVP connected bez od razu własnej fabryki",
      description:
        "Dla hardware startujemy od DevKit, firmware i aplikacji — walidujesz produkt, zanim płacisz za PCB i CE.",
    },
    {
      Icon: CheckCircle,
      title: "Transparentna wycena i model współpracy",
      description:
        "Stała cena przy domkniętym zakresie, Time & Material gdy discovery trwa w trakcie — często łączymy oba. Orientacyjne widełki: strona cennika MVP.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Co obejmuje budowa MVP aplikacji lub urządzenia",
    p: "Od discovery po launch pierwszej wersji. Zakres dopasowujemy do etapu: warsztaty, PoC, MVP albo V1 pod inwestora.",
  },
  services: [
    {
      Icon: Search,
      title: "Discovery i definicja zakresu MVP",
      description:
        "Mapujemy użytkowników, hipotezy i ryzyka. Ustalamy kryteria sukcesu oraz to, co świadomie zostaje poza MVP.",
    },
    {
      Icon: PenTool,
      title: "UX/UI pod pierwszą walidację",
      description:
        "Makiety i UI wystarczające do testu — bez overdesignu. Pracujemy też z gotową Figmą od klienta.",
    },
    {
      Icon: Smartphone,
      title: "MVP aplikacji mobilnej (Flutter / natywna)",
      description:
        "iOS i Android: logowanie, kluczowa ścieżka, powiadomienia, integracje. Flutter, gdy liczy się czas i jeden codebase.",
    },
    {
      Icon: Code,
      title: "MVP aplikacji webowej i SaaS",
      description:
        "PWA, panel admina, role użytkowników, płatności, API — typowe elementy pierwszej wersji platformy B2B lub B2C.",
    },
    {
      Icon: Sparkles,
      title: "MVP z AI / LLM",
      description:
        "Integracja OpenAI lub innych modeli: rekomendacje, asystent, klasyfikacja — w wąskim zakresie pod hipotezę biznesową.",
    },
    {
      Icon: Cpu,
      title: "MVP urządzenia IoT / BLE",
      description:
        "DevKit + firmware + aplikacja. Wearable, czujnik, sterownik — walidacja produktu connected przed własną PCB.",
    },
    {
      Icon: Rocket,
      title: "Launch, feedback i kolejne iteracje",
      description:
        "Wdrożenie, zbieranie feedbacku i plan V1.1 — zamiast „wersji finalnej” przy pierwszym release.",
    },
    {
      Icon: LifeBuoy,
      title: "Handover albo dalszy rozwój",
      description:
        "Dokumentacja i przekazanie zespołowi in-house albo dalszy development z nami po walidacji.",
    },
  ],
  cta: {
    h4: "Nie wiesz, od czego zacząć?",
    p: "Opisz pomysł w kilku zdaniach — zaproponujemy warsztaty, wycenę albo od razu zakres pierwszego MVP.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak powstaje MVP aplikacji lub urządzenia",
    p: "Od konsultacji po feedback z rynku. Przejrzyste etapy i mierzalne kryteria sukcesu — bez wielomiesięcznego waterfalla.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "30 minut: pomysł, użytkownicy, budżet, timeline. Decydujemy, czy idziemy w wycenę, warsztaty czy od razu PoC.",
    },
    {
      Icon: FileText,
      title: "Warsztaty lub brief",
      duration: "w kilka dni",
      description:
        "Domknięcie zakresu MVP, user stories i ryzyk. Przy gotowej Figmie skracamy ten etap.",
    },
    {
      Icon: PenTool,
      title: "Oferta i model współpracy",
      duration: "ustalenie harmonogramu",
      description:
        "NDA jeśli potrzeba, wycena, stała cena lub Time & Material, kamienie milowe i kryteria akceptacji.",
    },
    {
      Icon: Code,
      title: "Budowa wąskiego MVP",
      duration: "min. 6–12 tygodni",
      description:
        "Development kluczowej ścieżki użytkownika — web, mobile lub firmware + app równolegle.",
    },
    {
      Icon: Smartphone,
      title: "Testy wewnętrzne i polish",
      duration: "1–2 tygodnie",
      description:
        "Stabilność, UX krytycznych flow, podstawowe testy przed oddaniem użytkownikom.",
    },
    {
      Icon: Users,
      title: "Walidacja z użytkownikami",
      duration: "2–4 tygodnie",
      description:
        "Feedback z rynku, lista zmian do V1.1 i decyzja: pivot, iteracja albo skalowanie.",
    },
    {
      Icon: Rocket,
      title: "Launch i kolejne iteracje",
      duration: "min. 2–4 tygodnie",
      description:
        "Wdrożenie produkcyjne, monitoring i plan rozwoju po pierwszej walidacji.",
    },
    {
      Icon: CheckCircle,
      title: "Rozwój produktu lub handover",
      duration: "na stałe",
      description:
        "Dalsze sprinty produktowe albo przekazanie kodu i dokumentacji Waszemu zespołowi.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów budowę MVP",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  imageAlt: "Zespół Blues Brackets — MVP aplikacji lub urządzenia",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o MVP aplikacji lub urządzenia",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Chcesz szybko zwalidować pomysł?",
    p: "Umów się na darmową konsultację — opowiedz o aplikacji lub urządzeniu, a zaproponujemy zakres pierwszego MVP.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "MVP aplikacji mobilnej, webowej, SaaS lub urządzenia IoT. Polski software house — walidacja pomysłu bez przepalania budżetu.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      { href: "/mvp", text: "Budowa MVP" },
      ...mvpClusterLinks.filter(
        l => l.href !== "/mvp" && l.href !== "/mvp-aplikacji-lub-urzadzenia",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...mvpKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
